import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import {
    ArrowLeft, Save, Play, Workflow, FileJson, History, ListChecks, Settings2,
    CheckCircle2, XCircle, Clock, DollarSign, X, AlertCircle,
    Users, RotateCcw, Zap, Network, Rocket, Sparkles,
} from "lucide-react";
import FlowCanvas from "@/components/flow-designer/FlowCanvas";
import FlowPublishTab from "@/components/FlowPublishTab";

const TABS = [
    { key: "config", label: "Config", icon: Settings2 },
    { key: "designer", label: "Designer", icon: Network },
    { key: "versions", label: "Versions", icon: History },
    { key: "runner", label: "Runner", icon: Play },
    { key: "runs", label: "Runs", icon: ListChecks },
    { key: "publish", label: "Publish", icon: Rocket },
];

export default function FlowEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState("config");
    const [flow, setFlow] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [describing, setDescribing] = useState(false);
    const [versionNote, setVersionNote] = useState("");
    const [validation, setValidation] = useState(null);
    const [agents, setAgents] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [f, ag] = await Promise.all([
                api.get(`/admin/flows/${id}`),
                api.get(`/admin/agents?limit=200`),
            ]);
            setFlow(f.data);
            setDraft(JSON.parse(JSON.stringify(f.data)));
            setAgents(ag.data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            navigate("/flows");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = useMemo(() => {
        if (!flow || !draft) return false;
        const keys = ["name", "description", "tags", "status", "nodes", "edges", "input_schema", "output_schema"];
        return keys.some((k) => JSON.stringify(flow[k]) !== JSON.stringify(draft[k]));
    }, [flow, draft]);

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        try {
            const body = {
                name: draft.name,
                description: draft.description ?? null,
                tags: draft.tags || [],
                status: draft.status,
                nodes: draft.nodes || [],
                edges: draft.edges || [],
                input_schema: draft.input_schema || {},
                output_schema: draft.output_schema || {},
                version_note: versionNote || null,
            };
            const { data } = await api.patch(`/admin/flows/${id}`, body);
            setFlow(data);
            setDraft(JSON.parse(JSON.stringify(data)));
            setVersionNote("");
            toast.success(`Saved · v${data.active_version_no}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const validate = async () => {
        try {
            const { data } = await api.post(`/admin/flows/${id}/validate`);
            setValidation(data);
            if (data.ok) toast.success(`Flow valid · ${data.batches.length} batches`);
            else toast.error(`${data.errors.length} error(s)`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const aiDescribe = async () => {
        setDescribing(true);
        try {
            const { data } = await api.post(`/admin/flows/${id}/ai-describe`);
            // Apply to draft (will be saved by user with the standard Save button)
            setDraft((d) => ({
                ...d,
                description: data.description || d.description,
                tags: Array.from(new Set([...(d.tags || []), ...(data.tags || [])])).slice(0, 10),
            }));
            toast.success("AI description applied — review then Save");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setDescribing(false);
        }
    };

    if (loading) return <div className="p-8 text-white/40 text-sm">Loading…</div>;
    if (!flow || !draft) return null;

    return (
        <div className="p-6 space-y-5" data-testid="flow-editor">
            <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                    <Link to="/flows" className="mt-1 text-white/40 hover:text-white"><ArrowLeft className="w-4 h-4" /></Link>
                    <div>
                        <div className="flex items-center gap-2">
                            <Workflow className="w-4 h-4 text-[#7C3AED]" />
                            <h1 className="font-display text-xl tracking-tight" data-testid="flow-title">{flow.name}</h1>
                            <span className="font-mono text-[10px] text-white/40">v{flow.active_version_no} · {flow.slug}</span>
                            <StatusPill status={flow.status} />
                        </div>
                        <div className="text-xs text-white/50 mt-1">{flow.description || <span className="italic">No description</span>}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={aiDescribe} disabled={describing} data-testid="flow-ai-describe"
                        className="px-3 py-2 border border-[#7C3AED]/40 bg-[#7C3AED]/5 text-[#a78bfa] rounded-md text-xs font-medium hover:bg-[#7C3AED]/15 disabled:opacity-50 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> {describing ? "Describing…" : "AI describe"}
                    </button>
                    <button onClick={validate} data-testid="flow-validate"
                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5" /> Validate
                    </button>
                    {dirty && (
                        <input value={versionNote} onChange={(e) => setVersionNote(e.target.value)}
                            placeholder="Version note (optional)" data-testid="flow-version-note"
                            className="bg-[#0a0a0a] border border-white/15 rounded-md px-3 py-2 text-xs w-56" />
                    )}
                    <button onClick={save} disabled={!dirty || saving} data-testid="flow-save"
                        className={`px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                            dirty
                                ? "bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
                                : "bg-white/5 text-white/40 cursor-not-allowed"
                        }`}>
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : dirty ? "Save (new version)" : "Saved"}
                    </button>
                </div>
            </div>

            {validation && (
                <div className={`border rounded-md p-3 text-xs ${validation.ok ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-400" : "border-red-400/30 bg-red-400/5 text-red-400"}`}
                    data-testid="flow-validation">
                    {validation.ok
                        ? <>✓ Valid · {validation.batches.length} execution batches (parallel groups collapsed)</>
                        : <>✗ {validation.errors.join(" · ")}</>
                    }
                    {validation.warnings.length > 0 && <div className="text-amber-400 mt-1">⚠ {validation.warnings.join(" · ")}</div>}
                </div>
            )}

            <div className="border-b border-white/10 flex gap-1">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            data-testid={`flow-tab-${t.key}`}
                            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-md ${
                                tab === t.key
                                    ? "bg-[#0a0a0a] text-white border-x border-t border-white/10"
                                    : "text-white/40 hover:text-white/80"
                            }`}>
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            <div>
                {tab === "config" && <ConfigTab draft={draft} setDraft={setDraft} />}
                {tab === "designer" && (
                    <div className="mt-5">
                        <FlowCanvas flow={flow} draft={draft} setDraft={setDraft} agents={agents} />
                    </div>
                )}
                {tab === "versions" && <VersionsTab flowId={id} flow={flow} reload={load} />}
                {tab === "runner" && <RunnerTab flow={flow} draft={draft} setDraft={setDraft} agents={agents} />}
                {tab === "runs" && <RunsTab flowId={id} />}
                {tab === "publish" && <FlowPublishTab flow={flow} reload={load} />}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        draft: "bg-amber-400/10 border-amber-400/30 text-amber-400",
        active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
        archived: "bg-white/5 border-white/20 text-white/40",
    };
    return <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[status] || ""}`}>{status}</span>;
}

function ConfigTab({ draft, setDraft }) {
    return (
        <div className="grid grid-cols-2 gap-5 mt-5">
            <Section title="Identity">
                <Field label="Name">
                    <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="input" data-testid="flow-cfg-name" />
                </Field>
                <Field label="Status">
                    <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="input" data-testid="flow-cfg-status">
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="archived">Archived</option>
                    </select>
                </Field>
                <Field label="Description">
                    <textarea value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} className="input" />
                </Field>
                <Field label="Tags (comma-separated)">
                    <input value={(draft.tags || []).join(", ")}
                        onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        className="input text-xs" />
                </Field>
            </Section>
            <div className="space-y-4">
                <Section title="Input / output schemas">
                    <Field label="Input schema (JSON)">
                        <JsonEditor value={draft.input_schema || {}} onChange={(v) => setDraft({ ...draft, input_schema: v })}
                            testId="flow-cfg-input-schema" rows={10} />
                    </Field>
                    <Field label={<>Output schema (JSON; use <code className="text-[#7C3AED] px-1 bg-white/5">{"mapping"}</code> key to reshape final output)</>}>
                        <JsonEditor value={draft.output_schema || {}} onChange={(v) => setDraft({ ...draft, output_schema: v })}
                            testId="flow-cfg-output-schema" rows={10} />
                    </Field>
                </Section>
            </div>
        </div>
    );
}

function VersionsTab({ flowId, flow, reload }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get(`/admin/flows/${flowId}/versions`);
        setVersions(data.items || []);
        if (data.items?.length) setSelected(data.items[0]);
        setLoading(false);
    };
    useEffect(() => { load(); }, [flowId]); // eslint-disable-line react-hooks/exhaustive-deps

    const rollback = async (vno) => {
        if (!window.confirm(`Roll back to v${vno}? A new forward version will be created.`)) return;
        try {
            await api.post(`/admin/flows/${flowId}/versions/${vno}/rollback`);
            toast.success(`Rolled back to v${vno}`);
            await reload();
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    if (loading) return <div className="p-4 text-white/40 text-sm">Loading…</div>;
    return (
        <div className="grid grid-cols-3 gap-5 mt-5">
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md max-h-[600px] overflow-y-auto">
                {versions.map((v) => (
                    <button key={v.id} onClick={() => setSelected(v)}
                        data-testid={`flow-version-row-${v.version_no}`}
                        className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] ${selected?.id === v.id ? "bg-white/5" : ""}`}>
                        <div className="flex items-center justify-between">
                            <div className="font-mono text-xs">v{v.version_no}</div>
                            {flow.active_version_no === v.version_no && (
                                <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full border border-emerald-400/30 text-emerald-400">active</span>
                            )}
                        </div>
                        <div className="text-[10px] text-white/40 mt-0.5">{v.note || "—"}</div>
                        <div className="font-mono text-[10px] text-white/30 mt-1">{new Date(v.created_at).toLocaleString()}</div>
                    </button>
                ))}
            </div>
            <div className="col-span-2">
                {selected ? (
                    <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-sm">v{selected.version_no} — {selected.note}</div>
                            {flow.active_version_no !== selected.version_no && (
                                <button onClick={() => rollback(selected.version_no)} data-testid={`flow-rollback-${selected.version_no}`}
                                    className="px-3 py-1.5 bg-amber-400/10 border border-amber-400/30 text-amber-400 rounded-md text-xs font-medium hover:bg-amber-400/20 flex items-center gap-1.5">
                                    <RotateCcw className="w-3.5 h-3.5" /> Roll back to this version
                                </button>
                            )}
                        </div>
                        <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[10px] font-mono max-h-[500px] overflow-auto">
                            {JSON.stringify(selected.nodes, null, 2)}
                        </pre>
                    </div>
                ) : (
                    <div className="text-white/40 text-sm">Select a version.</div>
                )}
            </div>
        </div>
    );
}

function RunnerTab({ flow, draft, agents }) {
    const [input, setInput] = useState(JSON.stringify({}, null, 2));
    const [clientId, setClientId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [studyId, setStudyId] = useState("");
    const [running, setRunning] = useState(false);
    const [run, setRun] = useState(null);
    const [error, setError] = useState(null);

    const execute = async () => {
        setError(null);
        let payload;
        try { payload = JSON.parse(input || "{}"); } catch (e) { return setError(`Invalid input JSON: ${e.message}`); }
        setRunning(true);
        try {
            const { data } = await api.post(`/admin/flows/${flow.id}/run`, {
                input_payload: payload,
                client_id: clientId || null,
                site_id: siteId || null,
                study_id: studyId || null,
            });
            setRun(data);
            toast.success("Flow run started · polling…");
            const pollId = data.id;
            const poll = async () => {
                try {
                    const { data: r } = await api.get(`/admin/flows/${flow.id}/runs/${pollId}`);
                    setRun(r);
                    if (["completed", "failed", "awaiting_approval", "cancelled"].includes(r.status)) {
                        toast[r.status === "completed" ? "success" : "error"](`Flow ${r.status}`);
                        return;
                    }
                    setTimeout(poll, 2500);
                } catch (_err) { /* ignore transient poll error — next tick will retry */ }
            };
            setTimeout(poll, 2000);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
        } finally {
            setRunning(false);
        }
    };

    const approve = async (decision) => {
        const reason = decision === "approved" ? "ok" : window.prompt("Rejection reason:");
        if (decision === "rejected" && !reason) return;
        try {
            const { data } = await api.post(`/admin/flows/${flow.id}/runs/${run.id}/approve`, { decision, reason });
            setRun(data);
            toast.success(`${decision}`);
            setTimeout(async () => {
                const { data: r } = await api.get(`/admin/flows/${flow.id}/runs/${run.id}`);
                setRun(r);
            }, 2000);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 space-y-4" data-testid="flow-runner">
            {/* Visual canvas with live execution overlay */}
            <FlowCanvas flow={flow} draft={draft} setDraft={() => {}} agents={agents} execution={run} readOnly />

            {/* Run controls + output */}
            <div className="grid grid-cols-2 gap-5">
                <div className="space-y-3">
                    <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4">
                        <div className="label-xs mb-2">Input payload</div>
                        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={6}
                            className="input font-mono text-[11px]" data-testid="flow-runner-input" />
                    </div>
                    <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4">
                        <div className="label-xs mb-2">Tenancy (optional)</div>
                        <div className="grid grid-cols-3 gap-2">
                            <ClientPicker
                                value={clientId}
                                onChange={setClientId}
                                compact
                                width="w-full"
                                anyOptionLabel="(any client)"
                                testId="flow-runner-client"
                            />
                            <input value={siteId} onChange={(e) => setSiteId(e.target.value)} placeholder="Site ID" className="input text-xs font-mono" />
                            <input value={studyId} onChange={(e) => setStudyId(e.target.value)} placeholder="Study ID" className="input text-xs font-mono" />
                        </div>
                    </div>
                    <button onClick={execute} disabled={running} data-testid="flow-runner-run"
                        className="w-full px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#6D28D9] disabled:opacity-50 flex items-center justify-center gap-2">
                        <Play className="w-4 h-4" /> {running ? "Starting…" : "Run flow"}
                    </button>
                    <Link to={`/scheduled-tasks?new=flow&target=${flow.id}`} data-testid="flow-runner-schedule"
                        className="w-full px-4 py-2 border border-white/15 rounded-md text-xs text-white/80 hover:bg-white/5 flex items-center justify-center gap-2">
                        <Clock className="w-3.5 h-3.5" /> Schedule this flow
                    </Link>
                    {error && <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3">{error}</div>}
                </div>
                <div className="space-y-3">
                    {!run ? (
                        <div className="border border-dashed border-white/10 rounded-md p-10 text-center text-white/40 text-sm">Run to see the canvas light up node-by-node.</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-4 gap-3">
                                <Stat label="Status" value={run.status} color={run.status === "completed" ? "text-emerald-400" : run.status === "failed" ? "text-red-400" : "text-amber-400"} />
                                <Stat label="Steps" value={`${run.totals?.steps_completed || 0}/${run.totals?.steps_total || 0}`} />
                                <Stat label="Latency" value={`${run.totals?.latency_ms || 0} ms`} icon={<Clock className="w-3 h-3" />} />
                                <Stat label="Cost" value={`$${(run.totals?.cost_usd || 0).toFixed(5)}`} icon={<DollarSign className="w-3 h-3" />} />
                            </div>
                            {run.error && <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3">{run.error}</div>}
                            {run.status === "awaiting_approval" && (
                                <div className="border border-amber-400/30 bg-amber-400/5 rounded-md p-3 space-y-2">
                                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                                        <Users className="w-4 h-4" /> Awaiting approval for node <code className="text-[#7C3AED]">{run.awaiting_node_id}</code>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => approve("approved")} data-testid="flow-runner-approve"
                                            className="px-3 py-1.5 bg-emerald-400/10 border border-emerald-400/30 text-emerald-400 rounded-md text-xs font-medium hover:bg-emerald-400/20">
                                            Approve
                                        </button>
                                        <button onClick={() => approve("rejected")} data-testid="flow-runner-reject"
                                            className="px-3 py-1.5 bg-red-400/10 border border-red-400/30 text-red-400 rounded-md text-xs font-medium hover:bg-red-400/20">
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            )}
                            <NodeExecutionList runId={run.id} flowId={flow.id} run={run} />
                            <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-3">
                                <div className="label-xs mb-2">Output</div>
                                <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] font-mono max-h-60 overflow-auto" data-testid="flow-runner-output">
                                    {run.output_payload ? JSON.stringify(run.output_payload, null, 2) : "—"}
                                </pre>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function NodeExecutionList({ flowId, runId, run }) {
    const execs = run.node_executions || [];
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md" data-testid="flow-node-execs">
            <div className="px-4 py-2 label-xs text-white/40 border-b border-white/10">Node executions ({execs.length})</div>
            {execs.length === 0 ? (
                <div className="p-4 text-xs text-white/40 text-center">No executions yet…</div>
            ) : (
                <ol className="divide-y divide-white/5">
                    {execs.map((ne) => (
                        <li key={ne.id} className="px-4 py-2 flex items-center gap-3 text-xs">
                            <StatusIcon status={ne.status} />
                            <div className="flex-1 min-w-0">
                                <div className="truncate">{ne.node_name}</div>
                                <div className="font-mono text-[10px] text-white/40">{ne.node_type} · attempt {ne.attempt}</div>
                            </div>
                            <div className="font-mono text-[10px] text-white/40 whitespace-nowrap">
                                {ne.latency_ms || 0} ms · ${(ne.cost_usd || 0).toFixed(5)}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}

function StatusIcon({ status }) {
    if (status === "completed") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
    if (status === "failed") return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    if (status === "skipped") return <AlertCircle className="w-3.5 h-3.5 text-white/40" />;
    if (status === "awaiting_approval") return <Users className="w-3.5 h-3.5 text-amber-400" />;
    return <Clock className="w-3.5 h-3.5 text-white/40" />;
}

function RunsTab({ flowId }) {
    const [runs, setRuns] = useState([]);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get(`/admin/flows/${flowId}/runs?limit=100`);
        setRuns(data.items || []);
        setLoading(false);
    };
    useEffect(() => { load(); }, [flowId]); // eslint-disable-line react-hooks/exhaustive-deps

    const show = async (id) => {
        const { data } = await api.get(`/admin/flows/${flowId}/runs/${id}`);
        setDetail(data);
    };

    return (
        <div className="mt-5 space-y-3">
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                        <tr className="text-left label-xs text-white/40">
                            <th className="px-4 py-3">When</th>
                            <th className="px-4 py-3">v</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Steps</th>
                            <th className="px-4 py-3">Latency</th>
                            <th className="px-4 py-3">Cost</th>
                            <th className="px-4 py-3">Tenant</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">Loading…</td></tr>
                        ) : runs.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">No runs yet.</td></tr>
                        ) : runs.map((r) => (
                            <tr key={r.id} onClick={() => show(r.id)}
                                data-testid={`flow-run-row-${r.id}`}
                                className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer">
                                <td className="px-4 py-3 font-mono text-[10px] text-white/60">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="px-4 py-3 font-mono text-xs">v{r.version_no}</td>
                                <td className="px-4 py-3"><RunStatus status={r.status} /></td>
                                <td className="px-4 py-3 font-mono text-xs">{r.totals?.steps_completed || 0}/{r.totals?.steps_total || 0}</td>
                                <td className="px-4 py-3 font-mono text-xs">{r.totals?.latency_ms || 0} ms</td>
                                <td className="px-4 py-3 font-mono text-xs">${(r.totals?.cost_usd || 0).toFixed(5)}</td>
                                <td className="px-4 py-3 font-mono text-[10px] text-white/60">
                                    {[r.client_id, r.site_id, r.study_id].filter(Boolean).join(" / ") || "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {detail && <RunDetailModal flowId={flowId} run={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function RunStatus({ status }) {
    const map = {
        completed: "text-emerald-400", failed: "text-red-400",
        running: "text-amber-400", queued: "text-white/40", awaiting_approval: "text-amber-400",
        cancelled: "text-white/40",
    };
    return <span className={`text-[11px] font-mono ${map[status] || ""}`}>{status}</span>;
}

function RunDetailModal({ flowId, run, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="flow-run-detail">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <ListChecks className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg tracking-tight">Run {run.id.slice(0, 8)}</div>
                    <button onClick={onClose} className="ml-auto text-white/60"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-4 gap-3">
                        <Stat label="Status" value={run.status} />
                        <Stat label="Steps" value={`${run.totals?.steps_completed}/${run.totals?.steps_total}`} />
                        <Stat label="Latency" value={`${run.totals?.latency_ms} ms`} />
                        <Stat label="Cost" value={`$${(run.totals?.cost_usd || 0).toFixed(5)}`} />
                    </div>
                    {run.error && <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3">{run.error}</div>}
                    <div className="border border-white/10 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] border-b border-white/10">
                                <tr className="text-left label-xs text-white/40">
                                    <th className="px-3 py-2">Node</th>
                                    <th className="px-3 py-2">Type</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2">Attempt</th>
                                    <th className="px-3 py-2">Latency</th>
                                    <th className="px-3 py-2">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(run.node_executions || []).map((ne) => (
                                    <tr key={ne.id} className="border-b border-white/5">
                                        <td className="px-3 py-2 text-xs">{ne.node_name}</td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-white/60">{ne.node_type}</td>
                                        <td className="px-3 py-2"><StatusIcon status={ne.status} /></td>
                                        <td className="px-3 py-2 font-mono text-xs">{ne.attempt}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{ne.latency_ms} ms</td>
                                        <td className="px-3 py-2 font-mono text-xs">${(ne.cost_usd || 0).toFixed(5)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {run.output_payload && (
                        <div>
                            <div className="label-xs mb-1">Output</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] font-mono max-h-60 overflow-auto">{JSON.stringify(run.output_payload, null, 2)}</pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// -------- shared --------
function Section({ title, children }) {
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-5 space-y-3">
            <div className="label-xs text-white/40">{title}</div>
            {children}
        </div>
    );
}
function Field({ label, children }) {
    return (
        <label className="block">
            <div className="label-xs text-white/40 mb-1.5">{label}</div>
            {children}
        </label>
    );
}
function Stat({ label, value, icon, color }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-[#050505]">
            <div className="label-xs text-white/40 flex items-center gap-1.5">{icon} {label}</div>
            <div className={`font-mono text-sm mt-0.5 ${color || ""}`}>{value}</div>
        </div>
    );
}
function JsonEditor({ value, onChange, rows = 10, testId }) {
    const [text, setText] = useState(JSON.stringify(value, null, 2));
    const [err, setErr] = useState(null);
    useEffect(() => { setText(JSON.stringify(value, null, 2)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const handle = (e) => {
        const v = e.target.value;
        setText(v);
        try {
            const parsed = JSON.parse(v || "null");
            setErr(null);
            onChange(parsed);
        } catch (exc) {
            setErr(exc.message);
        }
    };
    return (
        <>
            <textarea value={text} onChange={handle} rows={rows} className="input font-mono text-[11px]" data-testid={testId} />
            {err && <div className="text-[10px] text-red-400 mt-1">{err}</div>}
        </>
    );
}
